import type { ColumnReading } from '../../lib/dashboard/bodyFat';
import type { GoalProjection } from '../../lib/dashboard/goalProjection';
import type { ObjectModel } from '../../lib/types';
import { CoachCard } from '../../design-system/concept/coach-card';
import { Eyebrow } from '../../design-system/concept/eyebrow';
import { ProgressTimeline } from '../../design-system/progress/progress-timeline';
import { TwinView } from './twin-view';
import {
  formatBodyFat as pct,
  formatChange,
  formatCm,
  formatWeight as kg,
  metricChange,
  withAsOf,
} from '../../design-system/lib/metric-format';

// The year is included deliberately: a client scanning across a year boundary
// gives "27 Jun -> 26 Mar", which reads as backwards without it.
const shortDate = (d: Date) =>
  d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** Kilograms — dated when carried from an earlier scan — or a dash when none was weighed. */
const kgOr = (n?: number, asOf?: Date) => (n === undefined ? '—' : withAsOf(kg(n), asOf));

/** In place of a kilogram figure the goal column cannot have without a weight. */
const NEEDS_WEIGHT = 'needs a weighed scan';

/** A kilogram change, by the same rule as every other weight change. */
const kgDelta = (a?: number, b?: number) =>
  a === undefined || b === undefined ? undefined : formatChange('weight', metricChange('weight', a, b));

const Row: React.FC<{
  label: string;
  value: string;
  delta?: string;
  withDelta?: boolean;
  /** A value carried from another scan reads quieter than this scan's own. */
  carried?: boolean;
  /** A value that is not there, said in words rather than a dash. */
  muted?: boolean;
  testId?: string;
}> = ({ label, value, delta, withDelta, carried, muted, testId }) => (
  <>
    <span className="text-xs text-foreground-soft">{label}</span>
    <span
      data-testid={testId}
      className={`tabular text-xs ${
        muted
          ? 'text-muted-foreground'
          : value === '—'
            ? 'font-semibold text-muted-foreground'
            : carried ? 'text-foreground-soft' : 'font-semibold'
      }`}
    >
      {value}
    </span>
    {/* A delta column keeps its cell even when empty, or the grid shifts the
        next row's label into it. */}
    {(withDelta || delta !== undefined) && (
      <span
        data-testid={testId ? `${testId}-delta` : undefined}
        className="tabular text-xs font-semibold text-primary"
      >
        {delta ?? ''}
      </span>
    )}
  </>
);

/**
 * One of the three columns: label, badge, the twin pair, then the readings.
 *
 * The twins live inside the column rather than in a band above it. Splitting
 * them meant printing THEN / NOW / GOAL twice on the same screen, once over
 * the meshes and once over the numbers — the concept has one header per
 * column because there is one column.
 */
const Column: React.FC<{
  label: string;
  caption: string;
  tone?: 'muted' | 'primary' | 'projected';
  badge?: { text: string; projected?: boolean };
  model: ObjectModel | null;
  /** True for meshes from /generate, which carry the average pose's lean. */
  generated?: boolean;
  projected?: boolean;
  /** Hidden on phone: three twin pairs do not fit at 390px. */
  phoneHidden?: boolean;
  children: React.ReactNode;
}> = ({
  label,
  caption,
  tone = 'muted',
  badge,
  model,
  generated,
  projected,
  phoneHidden,
  children,
}) => (
  <div className={`flex-col gap-3 ${phoneHidden ? 'hidden md:flex' : 'flex'}`}>
    <div className="flex items-baseline justify-between gap-2">
      <div>
        <Eyebrow tone={tone}>{label}</Eyebrow>
        <div className="mt-1.5 text-[15px] font-semibold">{caption}</div>
      </div>
      {badge && (
        <span
          className={`shrink-0 rounded px-1.5 py-1 font-mono text-[8.5px] font-semibold uppercase tracking-wider ${
            badge.projected
              ? 'projected-label'
              : 'bg-primary text-primary-foreground'
          }`}
        >
          {badge.text}
        </span>
      )}
    </div>

    {/* 1.6 : 1, the concept's own proportion for front against side. Taller
        on a phone, where the columns stack: the body is fitted to the frame's
        height, so a 230px frame the width of the screen left a small figure
        in a lot of background. */}
    <div className="flex h-[340px] gap-2 md:h-[230px]">
      <TwinView
        model={model}
        projected={projected}
        generated={generated}
        className="flex-[1.6]"
      />
      <TwinView
        model={model}
        projected={projected}
        generated={generated}
        side
        className="flex-1"
      />
    </div>

    {children}
  </div>
);

/**
 * The concept's hero: where the client started, where they are, and the twin
 * they are working toward. The goal column is dashed and amber throughout —
 * it is generated, not measured.
 *
 * Each column is one scan, read as it is. Body fat and waist come from the
 * tape and are always there; weight and lean only when that scan was weighed.
 * This took the kilogram summary whole before, so a client whose scans were
 * unweighed had no hero at all — and one whose latest scan was unweighed had a
 * Now column dated and valued by the scan before it.
 */
export const ThenNowGoal: React.FC<{
  start: ColumnReading;
  now: ColumnReading;
  /** 0…1 toward the target, in points of body fat; null without a target. */
  progressFraction: number | null;
  /**
   * The target itself. Separate from `goal`, which is the projected body: that
   * is generated and can be on its way or have failed, and the column used to
   * read a missing projection as a missing target — "No target set" beside a
   * rail saying "Reach 16 %".
   */
  targetBodyFat?: number;
  goal: GoalProjection | null;
  /**
   * Measured waist, first scan and latest, in centimetres, with the change
   * from the shared pair (metricPair) so it matches every other surface.
   * `nowAsOf` is set when the latest waist is from an earlier scan than Now.
   */
  waist?: { start?: number; now?: number; change?: number; nowAsOf?: Date };
  /** Each intermediate scan's own progress fraction, for the timeline dots. */
  scanFractions?: number[];
  models?: { start: ObjectModel | null; now: ObjectModel | null; goal: ObjectModel | null };
}> = ({ start, now, progressFraction, targetBodyFat, goal, waist, scanFractions, models }) => {
  const unweighed = [
    start.weightKg === undefined ? 'first' : null,
    now.weightKg === undefined ? 'latest' : null,
  ].filter(Boolean);

  return (
    <CoachCard testId="then-now-goal" className="gap-4">
      {/* Three columns is a desktop shape. On a phone the same blocks stack
          and the arrows collapse — 1fr at 390px would be 110px wide.

          minmax(0,1fr) rather than 1fr: a bare 1fr is minmax(auto,1fr), so the
          230px twin pair sets each column's minimum and the card overflows its
          own width rather than the twins shrinking. */}
      <div className="flex flex-col gap-6 md:grid md:grid-cols-[minmax(0,1fr)_40px_minmax(0,1fr)_40px_minmax(0,1fr)] md:items-start md:gap-y-3">
        <Column
          label={`Then · ${shortDate(start.date)}`}
          caption="Start"
          model={models?.start ?? null}
          phoneHidden
        >
          <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 rounded-[10px] border border-divider p-3">
            <Row label="Weight" value={kgOr(start.weightKg)} />
            <Row label="Body fat" value={pct(start.bodyFatPercent)} testId="hero-start-body-fat" />
            {waist?.start !== undefined && <Row label="Waist" value={formatCm(waist.start)} />}
            <Row label="Lean" value={kgOr(start.leanKg)} />
          </div>
        </Column>

        <div className="hidden h-14 items-center justify-center text-xl text-border md:flex">
          →
        </div>

        <Column
          label={`Now · ${shortDate(now.date)}`}
          caption="Latest scan"
          tone="primary"
          badge={{ text: 'Measured' }}
          model={models?.now ?? null}
        >
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1.5 rounded-[10px] border border-primary p-3">
            <Row
              label="Weight"
              value={kgOr(now.weightKg, now.weighedOn)}
              carried={Boolean(now.weighedOn)}
              delta={kgDelta(start.weightKg, now.weightKg)}
              withDelta
              testId="hero-now-weight"
            />
            <Row
              label="Body fat"
              value={pct(now.bodyFatPercent)}
              delta={formatChange(
                'body_fat',
                metricChange('body_fat', start.bodyFatPercent, now.bodyFatPercent),
              )}
              testId="hero-now-body-fat"
            />
            {waist?.now !== undefined && (
              <Row
                label="Waist"
                value={withAsOf(formatCm(waist.now), waist.nowAsOf)}
                carried={Boolean(waist.nowAsOf)}
                delta={waist.change !== undefined ? formatChange('waist', waist.change) : undefined}
                withDelta
                testId="hero-now-waist"
              />
            )}
            <Row
              label="Lean"
              value={kgOr(now.leanKg, now.weighedOn)}
              carried={Boolean(now.weighedOn)}
              delta={kgDelta(start.leanKg, now.leanKg)}
              withDelta
            />
          </div>
        </Column>

        <div className="hidden h-14 items-center justify-center text-xl text-border md:flex">
          →
        </div>

        <Column
          label={
            targetBodyFat !== undefined ? `Goal · ${targetBodyFat} % body fat` : 'Goal · not set'
          }
          caption="Projected"
          tone="projected"
          badge={goal ? { text: 'Simulated', projected: true } : undefined}
          model={models?.goal ?? null}
          generated
          projected
        >
          {goal ? (
            <div className="projected-frame grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 p-3">
              {/* Weight and lean need a weighed scan; the body and its waist
                  are built from the target alone, so they are here either way. */}
              {goal.weightKg !== undefined ? (
                <Row label="Weight" value={`≈ ${kg(goal.weightKg)}`} />
              ) : (
                <Row label="Weight" value={NEEDS_WEIGHT} muted />
              )}
              <Row label="Body fat" value={pct(goal.targetBodyFat)} />
              {/* Measured off the generated mesh, by the same definition as
                  the readings above it. Absent rather than approximated when
                  it cannot be derived that way. */}
              <Row
                label="Waist"
                value={goal.waistCm !== null ? `≈ ${formatCm(goal.waistCm)}` : '—'}
              />
              {goal.leanHeldKg !== undefined ? (
                <Row label="Lean" value={`${kg(goal.leanHeldKg)} held`} />
              ) : (
                <Row label="Lean" value={NEEDS_WEIGHT} muted />
              )}
            </div>
          ) : targetBodyFat !== undefined ? (
            <div className="projected-frame grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 p-3">
              <Row label="Weight" value="—" />
              <Row label="Body fat" value={pct(targetBodyFat)} />
              <Row label="Waist" value="—" />
              <Row label="Lean" value="—" />
            </div>
          ) : (
            <div className="projected-frame p-3 text-xs text-muted-foreground">
              No target set for this client.
            </div>
          )}
        </Column>
      </div>

      {/* Progress is in points of body fat, so it needs a target, not a
          projected body — it shows for a client whose scans were unweighed. */}
      {targetBodyFat !== undefined && progressFraction !== null && (
        <ProgressTimeline
          fraction={progressFraction}
          scanFractions={scanFractions}
          weeks={Math.round(
            (now.date.getTime() - start.date.getTime()) / (7 * 24 * 3600 * 1000),
          )}
          targetBodyFat={targetBodyFat}
        />
      )}

      <p className="mt-2 text-[10.5px] text-muted-foreground">
        {unweighed.length > 0 &&
          `The ${unweighed.join(' and ')} scan${unweighed.length > 1 ? 's were' : ' was'} not weighed, so weight and lean are not shown for ${unweighed.length > 1 ? 'them' : 'it'}. `}
        {now.weighedOn &&
          `The latest scan was not weighed; its weight and lean are from ${shortDate(now.weighedOn)}, the last weighed scan. `}
        {now.leanKg === undefined
          ? 'The goal column is simulated from the target body fat; its weight needs a weighed scan. It is not a scan, and no date is forecast.'
          : "The goal column is a projection at the client's current lean mass. It is not a scan, and no date is forecast."}
      </p>
    </CoachCard>
  );
};

/** One scan as the retail hero reads it: its date and its tape. */
export interface TapeReading {
  date: Date;
  values: Partial<Record<string, number>>;
}

/**
 * The retail hero: the first scan against the latest, by the tape a size is
 * chosen on.
 *
 * ThenNowGoal without the fitness. No goal column, since retail sets no
 * target; no weight, body fat or lean, since retail neither asks for a weight
 * nor reads the body as fat and lean. The columns and twins are the same
 * ones, so the two products look like one app.
 */
export const ThenNow: React.FC<{
  start: TapeReading;
  now: TapeReading;
  /** The readings to compare, in display order. */
  tape: { key: string; label: string; unit: string }[];
  models?: { start: ObjectModel | null; now: ObjectModel | null };
}> = ({ start, now, tape, models }) => {
  // A zero is "not captured on this scan", as everywhere else in the app.
  const read = (r: TapeReading, key: string) => {
    const v = r.values[key];
    return typeof v === 'number' && v > 0 ? Math.round(v) : undefined;
  };
  const cm = (n?: number, unit = 'cm') => (n === undefined ? '—' : `${n} ${unit}`);

  return (
    <CoachCard testId="then-now" className="gap-4">
      <div className="flex flex-col gap-6 md:grid md:grid-cols-[minmax(0,1fr)_40px_minmax(0,1fr)] md:items-start md:gap-y-3">
        <Column
          label={`Then · ${shortDate(start.date)}`}
          caption="First scan"
          model={models?.start ?? null}
          phoneHidden
        >
          <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 rounded-[10px] border border-divider p-3">
            {tape.map((m) => (
              <Row key={m.key} label={m.label} value={cm(read(start, m.key), m.unit)} />
            ))}
          </div>
        </Column>

        <div className="hidden h-14 items-center justify-center text-xl text-border md:flex">
          →
        </div>

        <Column
          label={`Now · ${shortDate(now.date)}`}
          caption="Latest scan"
          tone="primary"
          badge={{ text: 'Measured' }}
          model={models?.now ?? null}
        >
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1.5 rounded-[10px] border border-primary p-3">
            {tape.map((m) => {
              const a = read(start, m.key);
              const b = read(now, m.key);
              return (
                <Row
                  key={m.key}
                  label={m.label}
                  value={cm(b, m.unit)}
                  delta={
                    a !== undefined && b !== undefined
                      ? formatChange(m.key, metricChange(m.key, a, b))
                      : undefined
                  }
                  withDelta
                />
              );
            })}
          </div>
        </Column>
      </div>
    </CoachCard>
  );
};
